import type { PowerMeter, PowerValueInterpretation } from '@/features/controller/configuration/models/appConfigSchema'
import { threePhaseActivePowerRegisterAddresses } from '@/features/controller/configuration/models/raspiProperties/modbus'
import type {
  LegacyModbusMeter,
  TillquistConfig,
} from '@/features/controller/configuration/models/raspiProperties/types'
import { ModbusPowerRegisterVariant } from '@/features/controller/configuration/models/raspiProperties/types'

const METER_ID_PATTERN = /^meter-(\d+)$/

/**
 * Pick the next sequential `meter-N` id not already present in the list.
 * Treats existing ids that don't match the pattern as opaque (still avoided
 * for collisions, but not counted toward the next number).
 */
export function nextAppConfigPowerMeterId(powerMeters: ReadonlyArray<{ id: string }>): string {
  const usedIds = new Set(powerMeters.map((meter) => meter.id))
  const usedNumbers = powerMeters
    .map((meter) => METER_ID_PATTERN.exec(meter.id)?.[1])
    .filter((match): match is string => match !== undefined)
    .map(Number)
  let next = usedNumbers.length === 0 ? 1 : Math.max(...usedNumbers) + 1
  while (usedIds.has(`meter-${next}`)) {
    next++
  }
  return `meter-${next}`
}

export function describeTillquistProperties(tillquistConfig: TillquistConfig) {
  return [tillquistConfig.powerEnabled && 'power metering', tillquistConfig.frequencyEnabled && 'frequency metering']
}

function renderValueInterpretation({ absolute, adjustBy, multiplyBy }: PowerValueInterpretation) {
  let v = 'values'
  if (multiplyBy !== undefined) v = multiplyBy < 0 ? `${v} * (${multiplyBy})` : `${v} * ${multiplyBy}`
  if (adjustBy !== undefined) v = adjustBy < 0 ? `${v} - ${Math.abs(adjustBy)}` : `${v} + ${adjustBy}`
  if (absolute) v = `abs(${v})`
  return v
}

export function describeAppConfigPowerMeterProperties(powerMeter: PowerMeter) {
  return [
    powerMeter['metering'] && 'metering config',
    powerMeter.values && renderValueInterpretation(powerMeter.values),
    powerMeter.movingAverage && `${powerMeter.movingAverage.windowPeriod} moving average`,
  ]
}

export function describeModbusMeterProperties(meter: LegacyModbusMeter) {
  const pRegs = meter.activePowerRegisters
  const { phase1, phase2, phase3 } = threePhaseActivePowerRegisterAddresses(
    pRegs?.address ?? Number.NaN,
    meter.powerRegistersDataType,
  )
  const otherRegs = [
    meter.reactivePowerRegister && 'reactive',
    meter.watchdogRegister && 'watchdog',
    meter.meterErrorRegister && 'meter error',
    meter.systemTimestampRegister && 'sys. timestamp',
    meter.dataTimestampRegister && 'data timestamp',
  ]
    .filter(Boolean)
    .join(', ')
  return [
    `device #${meter.deviceNumber}`,
    `unit id ${meter.unitId}`,
    pRegs &&
      (pRegs.variant === ModbusPowerRegisterVariant.total
        ? `power reg.: ${pRegs.address}`
        : `power regs.: ${phase1}, ${phase2}, ${phase3}`) + ` (${pRegs.registers})`,
    otherRegs && `other regs.: ${otherRegs}`,
  ]
}
